import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { Label } from 'reactstrap';
import Swal from 'sweetalert2';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { setSelectedAffiliatedEntityCode } from '../../redux/AffiliateSlice';
import {
    AffiliateReporting, Afr, clearFactsForAffiliate, FactTable, factValue,
    getSelectedAffiliateReport, getSelectedAfr, getTable, isCRAAuditRequired, loadAfr
} from '../../redux/AfrSlice';
import { loadAuditor, resetAuditorState } from '../../redux/AuditorSlice';
import { loadConcept } from '../../redux/ConceptSlice';
import { EditEntityQuery,loadEntity } from '../../redux/EntitySlice';
import {
    AffiliateAuditor, clearTableRow, getAffiliates, getAffiliatesAuditors,
    getAuditorFromAffiliate, getMiscAffiliate, getSelector, isDirty, resetAffiliateAuditors,
    setIsDirty, updateAffiliate, updateAffiliateAuditors
} from '../../redux/MiscSlice';
import { AdminUserRoleEnum, selectAdminUserRole, selectUserRole } from '../../redux/UserSlice';
import { AFRError, ErrorSummaryAFRSection, getErrorList } from '../../redux/ValidationSlice';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import { TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';


const CRA: string = "Community Redevelopment Agencies";
// Move this function out of the scope of the function component se we're not unintentionally picking local function state
const getTableData = function (afr: Afr | null, tableName: string): any[] {
    var dataRows: any[] = [];
    if (!afr) {
        return dataRows;
    }
    if (!afr.currentversion) {
        return dataRows;
    }
    var acctTable: FactTable | null = getTable(afr, tableName);
    if (!acctTable) {
        return dataRows;
    }
    if (!afr.affiliateReporting) {
        return dataRows;
    }
    var rowMapping: any = {};
    for (var affRep of afr.affiliateReporting) {
        if (!affRep.affiliateReference) {
            continue;
        }
        var accountLabel = affRep.affiliateReference.name;
        var rowCode = affRep.affiliateReference.code;
        var rowId = affRep.affiliateReference.id;
        var bIsCRA = (affRep.affiliateReference.category === CRA) ? true : false;
        var nextRow: any = {
            account: accountLabel,
            reporttype: affRep.reportingType,
            isCRA: bIsCRA,
            dimensionCode: rowCode,
            dimensionId: rowId
        }
        if (affRep.independentTotals) {
            for (var fInd of affRep.independentTotals) {
                var colcode = fInd.concept.id;
                nextRow[colcode] = fInd.value;
            }
        }
        dataRows.push(nextRow);
        rowMapping[rowId] = nextRow;

    }

    if (acctTable.rows && acctTable.rows.length > 0) {

        for (var row of acctTable.rows) {
            var dimId = utilityHelper.getDimensionalAffiliateId(row.dimensions);
            if (rowMapping.hasOwnProperty(dimId)) {
                var nextRow = rowMapping[dimId];
                for (var fact of row.facts) {
                    var colcode = fact.concept.id;
                    nextRow[colcode] = fact.value;
                }
            }
        }
    }
    return dataRows;
}

const ColumnAccountEditorGrid = React.memo(
    function ColumnAccountEditorGrid(props: {
        tableName: string,
        gridData: any[], gridColumns: any[], gridOptions: any,
        cellEditHandler: any, doneFlag: string, noFlag: string
    }) {

        const firstFieldId = "BS_1xxxxx_AssetsOtherDebitsDeferredOutflow"
        /* ************************************** Redux ********************************************/
        const dispatch = useAppDispatch();
        const parameters = useLocation();
        var tabulatorRef = React.useRef<any>();
        const fieldToEdit = useRef(firstFieldId)
        const affiliatesAuditors = useRef(null);
        const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
        const auditRequired: boolean = useAppSelector(isCRAAuditRequired);
        const userRoleSelector = useAppSelector(selectUserRole);
        const isDirtyInfo: boolean = useAppSelector(isDirty);
        const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);

        /* ************************************** Local State **********************************************/
        const [dataKey, setDataKey] = useState("");
        const [tabledata, setTabledata] = useState(getTableData(currentAfr, props.tableName));
        const [forceRefresh, setForceRefresh] = useState(false);

        /* *************************************************************************************************/

        //**************************************************************************************************/
        // Loads the affiliates with auditor on redux to have a track into the grid
        // for each change of auditor. The array generated is saved into a reference
        // to avoid the closure issue
        //**************************************************************************************************/
        function getAffiliateAuditors() {

            if (currentAfr && currentAfr.affiliateReporting) {

                for (var affiliate of currentAfr.affiliateReporting) {
                    if (!affiliate.affiliateReference) {
                        continue;
                    }
                    var code = "";
                    if (affiliate.audit && affiliate.audit.auditorReference) code = affiliate.audit.auditorReference.code;

                    var aff: AffiliateAuditor = {
                        affiliateEntityCode: affiliate.affiliateReference.code,
                        affiliateName: affiliate.affiliateReference.name,
                        auditor: code
                    };
                    dispatch(updateAffiliateAuditors(aff));
                }
            }
        }

        const getEntityCodeFromUrl = function () {
            var returnValue = "";
            if (parameters && parameters.state && parameters.state.entityCode) { return parameters.state.entityCode }
            return returnValue;
        }

        // special logic to verify if a cell is editable or not
        // editable    --> return true
        // no editable --> return false
        const handleEditCheck = function (cell: any) {
            const f = cell.getColumn().getField();

            if (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin) {
                return false;
            }

            if (f !== "reporttype") {
                var alertMessage : string | null = null;
                var reporttype = cell.getRow().getData().reporttype;
                switch (reporttype) {
                    case enums.affiliatedEntityReportingType.notSpecified:
                        alertMessage = "To edit this cell you must specify a reporting type of Blended, Discrete or Zero";
                        break;

                    case enums.affiliatedEntityReportingType.discrete:
                    case enums.affiliatedEntityReportingType.blended:
                        break;

                    case enums.affiliatedEntityReportingType.zero:
                        switch (f) {
                            case enums.affiliatedEntityTotalZeroField.totalRevenues:
                            case enums.affiliatedEntityTotalZeroField.totalExpenditures:
                                alertMessage = "For Zero reporting the " + enums.affiliatedEntityTotalZeroField[f] + " column must be 0 and is not editable";
                                break;
                        }
                        break;

                    case enums.affiliatedEntityReportingType.independent:
                        alertMessage = "Total values cannot be supplied for independently reported entities";
                        break;
                    case enums.affiliatedEntityReportingType.nonComponentUnit:
                        alertMessage = "Total values cannot be supplied for non component entities";
                        break;
                }
                if (alertMessage) {
                    //alert(alertMessage);
                    console.log(alertMessage);
                    return false;
                }
            }

            return !(utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified));
        }

        const getValue = (e: any) => {
            var result = parseInt(e);
            if (isNaN(result)) return 0;
            return result;
        }

        const handleHeaderClick = function (e: any, column: any) {
            // get the column name to pull the concept associated to it
            var accountId = column.getField();

            if (accountId && accountId != "reporttype") {
                dispatch(loadConcept({
                    conceptId: accountId,
                    taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                    afrYear: currentAfr ? currentAfr.year : 2019
                }));
            }

        }

        const handleCellClick = function (e: any, cell: any) {
            //e - the click event object
            //cell - cell component
            var row = cell.getRow();
            if (row._row.type != "row") { return }

//            row.scrollTo();


            var accountId: string = cell.getField();
            if (accountId.includes("account") || accountId.includes("reporttype")) { return }

            if (cell.getField() != fieldToEdit.current) {
                fieldToEdit.current = cell.getField();
            }

            if (accountId) {
                dispatch(loadConcept({
                    conceptId: accountId,
                    taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                    afrYear: currentAfr ? currentAfr.year : 2019
                }));
            }
        }

        const updateCell = function (cell: any) {

            var errorMessage: Array<AFRError> = [];

            var table = cell.getTable();

            // Track the changes on Cells to an array of facts into MiscSlice
            utilityHelper.updateTableCell(props.tableName, null, cell, table, dispatch);
            utilityHelper.isAffiliatedEntitySatisfied(currentAfr, errorMessage, dispatch);
            if (currentAfr) {
                utilityHelper.notifyErrors(currentAfr, enums.UISection.affiliatedEntities, errorMessage, dispatch);
            }

            var row = cell.getRow();
            row.reformat();
            // ***************************************************************************

        }

        const handleEdited = function (cell: any) {
            if (cell.getField() === "reporttype") {
                return;
            }
            var newValue: string = cell.getValue();
            newValue = newValue.trim();

            if ((getValue(cell.getOldValue()) != getValue(newValue)) || (newValue.length > 0 && getValue(newValue) === 0)) {
                // Turn ON the dirty flag in redux
                dispatch(setIsDirty({ IsDirty: true, Page: "" }));
                var dimensionId = cell.getRow().getData().dimensionId;
                //setDataKey(dimensionId);
                // get next cell to right
                fieldToEdit.current = getNextCell(cell);
                //props.cellEditHandler(cell);
                /* Update the cell into miscSlice and run the validation to notify any error */
                updateCell(cell);
                // *************************************************************************** 
            }

        }



        const handleRowClick = function (e: any, row: any) {
            //e - the click event object
            //row - row component
            if (!(row)) {
                return;
            }
            if (row._row.type != "row") { return }

            row.getTable().deselectRow();
            row.select();

            var dimensionId = row.getData().dimensionId;
            var code = row.getData().dimensionCode;

            if (currentAfr &&
                currentAfr.affiliateReporting) {

                var affiliateAuditor: AffiliateAuditor | null = null;
                affiliatesAuditors.current = dispatch(getSelector(getAffiliatesAuditors));

                if (affiliatesAuditors.current) {
                    affiliateAuditor = getAuditorFromAffiliate(affiliatesAuditors.current, code);
                }

                if (affiliateAuditor && affiliateAuditor.auditor.length > 0) {
                    dispatch(loadAuditor(affiliateAuditor.auditor));
                } else {
                    dispatch(resetAuditorState({}));
                }

            } else {
                dispatch(resetAuditorState({}));
            }


            // Load the current affiliated entity code to redux to be used into other components
            dispatch(setSelectedAffiliatedEntityCode(code));
            // setDataKey(dimensionId);

        }

        // Search a row component given a Dimension Code
        const getRowFromDimensionId = function (table : any, dimension: string) {
            if (dimension.length === 0) { return null }
           
            var rows: any[];

            rows = table.searchRows("dimensionId", "=", dimension);
            if (rows && rows.length > 0) {
                return rows[0];
            }

            return null;

        }

        // get next row to right to be edited
        const getNextCell = function (cell: any) {
            if (!cell) { return "" };

            return cell.getField();
        }


        const rptTypeOptions = {
            "NONCOMPO": "Not a Component Unit",
            "BLENDED": "Blended in Primary Report",
            "INDEPEND": "Independently Reported",
            "DISCRETE": "Discretely Reported",
            "ZEROREPT": "Zero Revenues and Expenditures"
        };

        const setCellValue = function (cell: any, field: string, value: string) {
            var row = cell.getRow();
            var updCell = row.getCell(field);
            if (!updCell) {
                return;
            }
            // Note we use the double = to test exlusively for null or undefined
            if (value.length === 0 && updCell.getValue() == null) {
                return;
            }
            updCell.setValue(value);
            updateCell(updCell);
        }

        const clearRowValues = function (row: any, fieldsToClear: string[]) {
            var errorMessage: Array<AFRError> = [];
            var rowFact: factValue | null = utilityHelper.abstractAffiliatedEntityRowFact(enums.tableName.affiliatedEntity, row);
            var rdata = row.getData();
            var affiliateId = rdata.dimensionId;
            var table = row.getTable();
            if (!table) {
                return;
            }

            var oldRows: any[] = table.getData();
            var newRows: any[] = [];
            // after update [{"account":"St. Lucie County Erosion District","reporttype":"NONCOMPO","isCRA":false,"dimensionCode":"300713","dimensionId":"uas-repunit_SD_300713_StLucieCountyErosionDistrict","Rev_3xxxxx_Revenue":1,"Exp_5xxxx_7xxxx_Expenditures":2,"AffiliateLongTermDebt":3},
            oldRows.map(function (oldrow, idx) {
                var newRow = { ...oldrow };
                if (oldrow.account === rdata.account) {
                    newRow = {
                        account: rdata.account,
                        reporttype: rdata.reporttype,
                        isCRA: rdata.isCRA,
                        dimensionCode: rdata.dimensionCode,
                        dimensionId: rdata.dimensionId
                    };
                }
                newRows.push(newRow);
            })
            setTabledata(newRows);
            // Track the changes on Cells to an array of facts into MiscSlice
            // Clear out the corresponding (if any) entries in the misc slice
            if (rowFact) {
                dispatch(clearTableRow(rowFact));
            }
            // Clear out any affiliate supplemental facts
            dispatch(clearFactsForAffiliate(affiliateId));
            // validate the affiliate state
            utilityHelper.isAffiliatedEntitySatisfied(currentAfr, errorMessage, dispatch);
            if (currentAfr) {
                utilityHelper.notifyErrors(currentAfr, enums.UISection.affiliatedEntities, errorMessage, dispatch);
            }
            setForceRefresh(true);
        }

        const reportingTypeMutator = function (value: any, data: any, type: any, params: any, component: any) {
            return value;

        }

        const composeSelectFormatter = function (options: any) {

            return (cell: any) => {
                var currentValue = cell.getValue();
                if (!currentValue) {
                    return "";
                }

                var oldValue = cell.getOldValue();

                if (oldValue && (oldValue != currentValue)) {

                    /* Here we have to save the Information */
                    /* Get the current affiliated entity from Redux */
                    var row = cell.getRow();
                    var code = row.getData().dimensionCode;
                    const miscAffiliates: Array<AffiliateReporting> = dispatch(getSelector(getAffiliates));
                    let affiliate: AffiliateReporting | null | undefined = getMiscAffiliate(miscAffiliates, code);

                    // If the Affiliate does not exits on Misc is because never has been touched. for this case get the affiliate from AFRSlice
                    if (!(affiliate)) {
                        affiliate = getSelectedAffiliateReport(currentAfr, code);
                    }

                    var updatedAffiliatedEntity: AffiliateReporting = JSON.parse(JSON.stringify(affiliate));
                    updatedAffiliatedEntity.reportingType = currentValue;

                    // Update in redux misc slice
                    dispatch(updateAffiliate(updatedAffiliatedEntity));

                    // Saving pending changes to AfrSlice
                    // NOTE This needs to be done before we change the reporting type, since we may end up clearing selected
                    // values from the AFR slice and we need it to reflect the current pending data
                    utilityHelper.saveDeferredChanges(dispatch);
                    // Update facts depending of reporting type
                    switch (updatedAffiliatedEntity.reportingType) {
                        case "INDEPEND": //"Independently Reported",
                        case "NONCOMPO": //"Not a component unit",
                            switch (oldValue) {
                                case "NOT_SPECIFIED":
                                case "INDEPEND":
                                case "NONCOMPO":
                                    break;

                                default:
                                    // Need to clear row data
                                    clearRowValues(row, [enums.affiateEntityColumn.revenues, enums.affiateEntityColumn.expenditures, enums.affiateEntityColumn.debt]);
                                    break;
                            }
                            //                            setCellValue(cell, enums.affiateEntityColumn.revenues, "");
                            //                            setCellValue(cell, enums.affiateEntityColumn.expenditures, "");
                            //                            setCellValue(cell, enums.affiateEntityColumn.debt, "");
                            break;
                        case "BLENDED": //"Blended in Primary Report",
                        case "DISCRETE": //"Discretely Reported",
                            break;

                        case "ZEROREPT": //"Zero Revenues and Expenditures"
                            setCellValue(cell, enums.affiateEntityColumn.revenues, "0");
                            setCellValue(cell, enums.affiateEntityColumn.expenditures, "0");
                            //                            setCellValue(cell, enums.affiateEntityColumn.debt, "0");
                            break;
                    }
                    dispatch(setIsDirty({ IsDirty: true, Page: "" }));

                }


                return options[currentValue];
            }
        }

        const handleSelectOptions = function (cell: any) {

            //create a options list of all names currently in the table
            var options : any[] = [];

            options.push({
                label: "Not a Component Unit",
                value: "NONCOMPO",
                disabled: false
            });

            options.push({
                label: "Blended in Primary Report",
                value: "BLENDED",
                disabled: false
            });

            options.push({
                label: "Independently Reported",
                value: "INDEPEND",
                disabled: false
            });

            options.push({
                label: "Discretely Reported",
                value: "DISCRETE",
                disabled: false
            });

            options.push({
                label: "Zero Revenues and Expenditures",
                value: "ZEROREPT",
                disabled: false
            });

            return options;
        }


        const newGridOptions = {
            layout: "fitColumns",
            scrollToRowIfVisible: false,
            layoutColumnsOnNewData: true,
            dataTree: true,
            selectable: 1,
            selectableRollingSelection: false,
            height: "300px",  // to be adjusted if another thing is decided
            maxHeight: "300px",
            // columnMaxWidth: 120,
            index: "dimensionId",
            cellEdited: handleEdited,
            cellClick: handleCellClick,
            ...props.gridOptions

        }

        // This is the previous code
        //
        const isMatchingAffiliateError = function (error: AFRError, tab: string, affiliateId: string): boolean {
            if (!affiliateId) {
                return false;
            }
            if (error.entityType !== tab) {
                if (!error.affiliateId) {
                    return false;
                }
            }
            if (error.entityCode === affiliateId) {
                return true;
            }
            if (!error.affiliateId) {
                return false;
            }
            return error.affiliateId === affiliateId;
        }
        const showErrorIcon = function (cell: any, textCell: string) {
            var text: string = textCell;
            var { section, tab } = utilityHelper.getSectionError(props.tableName);

            if ((typeof text) === "undefined") {
                return text;
            }

            /* get error list */
            const errorList: Array<ErrorSummaryAFRSection> = dispatch(getSelector(getErrorList));


            // Verify if there is an error in this section
            var errorSection: ErrorSummaryAFRSection | undefined = errorList.find(x => x.section.sectionCode === section);
            if ((typeof errorSection) === "undefined") {
                return text;
            }

            if (errorSection !== undefined) {
                // Discover the array of errors for the section
                var error = errorSection.errorList.find(x => isMatchingAffiliateError(x, tab, cell.getData().dimensionId));

                if ((typeof error) === "undefined") {
                    return text;
                } else {
                    if (error !== undefined) {
                        return "<i class='bi bi-x-octagon-fill text-danger' title='" + error.description + "'></i> " + text;
                    }
                }

            } else {
                return text;
            }
        };

        const showAENonComp = function (cell: any, formatterParams: any, onRendered: any) {
            var row = cell.getRow();
            var rowData = row.getData();
            var text: string | undefined = "";

            if (rowData.isCRA === true) {
                if (auditRequired) {
                    text = "<i class='bi bi-cone-striped text-danger' title='The Audit is required!'></i> " + text;
                } else {
                    text = "<i class='bi bi-cone-striped text-success'></i> " + text;
                }
            }

            text = showErrorIcon(cell, text);

            return text;
        };

    
        const defaultColumns: any = [];

        const addErrorIconJSX = function (cell: any, textCell: string, elements: JSX.Element[]) {
            var text: string = textCell;
            var { section, tab } = utilityHelper.getSectionError(props.tableName);

            if ((typeof text) === "undefined") {
                return elements;
            }

            /* get error list */
            const errorList: Array<ErrorSummaryAFRSection> = dispatch(getSelector(getErrorList));


            // Verify if there is an error in this section
            var errorSection: ErrorSummaryAFRSection | undefined = errorList.find(x => x.section.sectionCode === section);
            if ((typeof errorSection) === "undefined") {
                return elements;
            }

            // Discover the array of errors for the section
            var error = errorSection !== undefined ? errorSection.errorList.find(x => x.entityType === tab && x.entityCode === cell.getData().dimensionId) : undefined;

            if ((typeof error) === "undefined") {
                return elements;
            }

            var errorDescriptionValue = error !== undefined ? error.description : "";

            elements.unshift(<i className="bi bi-x-octagon-fill text-danger" title={errorDescriptionValue}></i>)

            return elements;

        };

        const history = useHistory();

        function AffiliatedEntityNameLink(props: any) {
            const rowData = props.cell._cell.row.data;
            const cellValue = props.cell._cell.value || 'Edit | Show';
            var result: JSX.Element[] = [];

            if (rowData.reporttype == "INDEPEND" || rowData.reporttype == "NONCOMPO") {
                result.push(<a
                    href="javascript:;"

                    onClick={() => {

                        if (isDirtyInfo) {
                            Swal.fire({
                                title: '<b>Are you sure?</b>',
                                html: "You are about to navigate to an affiliate AFR, which will"
                                    + " discard your changes.  Continue?",
                                icon: 'warning',
                                allowOutsideClick: false,
                                showCancelButton: true,
                                showDenyButton: false,
                                confirmButtonColor: '#3085d6',
                                denyButtonColor: '#28a745',
                                cancelButtonColor: '#d33',
                                confirmButtonText: 'Yes, continue!',
                                cancelButtonText: 'No, cancel!',
                                reverseButtons: true
                            }).then((result: any) => {

                                if (result.isConfirmed) {

                                    var year = currentAfr != null ? currentAfr.year : 0;
                                    var entityIdByYear = `${rowData.dimensionCode}_${year.toString()}`;


                                    dispatch(setIsDirty({ IsDirty: false, Page: "" }))
                                    dispatch(loadAfr(entityIdByYear));
                                    var eQuery: EditEntityQuery = {
                                        entityId: rowData.dimensionCode,
                                        taxonomyYear: year
                                    };
                                    dispatch(loadEntity(eQuery));

                                    if (userRoleSelector == "ADMIN") {
                                        history.push("/EntityAFRManagementA")
                                    }
                                    else if (userRoleSelector == "ENTITY_USER") {
                                        history.push("/EntityAFRManagement")
                                    }

                                    return true;
                                } else {

                                    return false;
                                }
                            });
                        }
                        else {
                            var year = currentAfr != null ? currentAfr.year : 0;
                            var entityIdByYear = `${rowData.dimensionCode}_${year.toString()}`;


                            dispatch(setIsDirty({ IsDirty: false, Page: "" }))
                            dispatch(loadAfr(entityIdByYear));
                            var eQuery: EditEntityQuery = {
                                entityId: rowData.dimensionCode,
                                taxonomyYear: year
                            };
                            dispatch(loadEntity(eQuery));

                            if (userRoleSelector == "ADMIN") {
                                history.push("/EntityAFRManagementA")
                            }
                            else if (userRoleSelector == "ENTITY_USER") {
                                history.push("/EntityAFRManagement")
                            }
                        }


                    }
                    } >{cellValue} </a>);

            }
            else {
                result.push(<Label>{cellValue}</Label>);
            }


            if (rowData.isCRA === true) {
                if (auditRequired) {
                    result.unshift(<i className="bi bi-cone-striped text-danger" title="The Audit is required!"></i>);
                }

                result.unshift(<i className="bi bi-cone-striped text-success"></i>);
            }

            addErrorIconJSX(props.cell, cellValue, result);
            return <React.Fragment> {result} </React.Fragment>;
        }

        defaultColumns.push(
            {
                title: "",
                headerHozAlign: "center",
                frozen: true,
                columns: [
                    {
                        title: "",
                        field: "status",
                        headerSort: false,
                        formatter: showAENonComp,
                        width: 50
                    },
                    {
                        title: "Affiliated Entities",
                        titleFormatter: "textarea",
                        field: "account",
                        formatter: "textarea", //reactFormatter(<AffiliatedEntityNameLink />),//showAENonComp,
                        width: 280
                    },
                ]
            }
        );
        // Adding report type column
        defaultColumns.push({
            headerHozAlign: "center",
            frozen: true,            
            title: "Reporting Type",
            titleFormatter: "textarea",
            field: "reporttype",
            headerSort: false,
            editor: "select",
            editable: handleEditCheck,
            editorParams: { values: rptTypeOptions },
            formatter: composeSelectFormatter(rptTypeOptions),
            hozAlign: "left",           
        });
        
        var acctTable: FactTable | null = null;
        if (currentAfr) {
            acctTable = getTable(currentAfr, props.tableName);

            if (acctTable) {
                // Note - it is possible for the requested account table to not be present in this AFR
                // However that should be deal with upstream so the option doe snot appear
                var colGroup : any[] = [];

                for (var tbeGroup of acctTable.structure.nthlevelcolumnHierarchy) {
                    var t : any | null = null;
                    var tGroup = "";
                    if (tbeGroup.sublevels) {
                        for (var tbeCol of tbeGroup.sublevels) {
                            t = tbeCol.conceptaspect ? tbeCol.conceptaspect.id : "na";
                            var titletext = tbeCol.label.replace("Total ", "");
                            colGroup.push({
                                title: titletext,
                                titleFormatter: "textarea",
                                field: t,
                                headerSort: false,
                                editor: "input",
                                cellEdited: handleEdited,
                                cellClick: handleCellClick,
                                editable: handleEditCheck,
                                headerClick: handleHeaderClick,
                                editorParams: { mask: "*999999999999" },
                                validator: "numeric",
                                headerHozAlign: "center",
                                hozAlign: "right",
                                formatter: "money",
                                formatterParams: { symbol: "$", precision: 0 },
                            });
                        }
//                        tGroup = "Affiliated Entities Totals";
                        tGroup = "Totals of Selected Accounts for Affiliated Entities";
                    } else {
                        t = tbeGroup.conceptaspect.id;
                        var titletext = tbeGroup.label;

                        colGroup.push({
                            title: titletext,
                            titleFormatter: "textarea",
                            field: t,
                            headerSort: false,
                            editor: "input",
                            cellEdited: handleEdited,
                            cellClick: handleCellClick,
                            editable: handleEditCheck,
                            headerClick: handleHeaderClick,
                            editorParams: { mask: "*999999999999" },
                            validator: "numeric",
                            headerHozAlign: "right",
                            hozAlign: "right",
                            formatter: "money", formatterParams: { symbol: "$", precision: 0 },
                        });
                    }
                    defaultColumns.push({
                        title: tGroup,
                        field: '',
                        headerHozAlign: "center",
                        columns: colGroup
                    });
                }
            }
        }

        var initialtabledata : any[] = [];
        if (acctTable && currentAfr && currentAfr.affiliateReporting) {
            var rowMapping: any = {};
            for (var affRep of currentAfr.affiliateReporting) {
                if (!affRep.affiliateReference) {
                    continue;
                }
                var accountLabel = affRep.affiliateReference.name;
                var rowCode = affRep.affiliateReference.code;
                var rowId = affRep.affiliateReference.id;
                var bIsCRA = (affRep.affiliateReference.category === CRA) ? true : false;
                var nextRow: any = {
                    account: accountLabel,
                    reporttype: affRep.reportingType,
                    isCRA: bIsCRA,
                    dimensionCode: rowCode,
                    dimensionId: rowId
                }
                if (affRep.independentTotals) {
                    for (var fInd of affRep.independentTotals) {
                        var colcode = fInd.concept.id;
                        nextRow[colcode] = fInd.value;
                    }
                }
                initialtabledata.push(nextRow);
                rowMapping[rowId] = nextRow;

            }

            if (acctTable.rows && acctTable.rows.length > 0) {

                for (var row of acctTable.rows) {
                    var dimId = utilityHelper.getDimensionalAffiliateId(row.dimensions);
                    if (rowMapping.hasOwnProperty(dimId)) {
                        var nextRow = rowMapping[dimId];
                        for (var fact of row.facts) {
                            var colcode = fact.concept.id;
                            nextRow[colcode] = fact.value;
                        }
                    }
                }
            }
        }

        // The very first time the affiliates and Auditors are moved to redux -> slice: "misc"
        React.useEffect(() => {
            dispatch(resetAffiliateAuditors);
            getAffiliateAuditors();

            tabulatorRef.current = new Tabulator("#grid_" + props.tableName, {
                data: tabledata,
                columns: defaultColumns,
                layout: "fitColumns",
                layoutColumnsOnNewData: true,
                selectable: 1,
                selectableRollingSelection: false,
                scrollToRowPosition: "center",

            }).on("rowClick", handleRowClick);

        }, []);


        // The very first time the affiliates and Auditors are moved to redux -> slice: "misc"
        React.useEffect(() => {
            if (forceRefresh) {
                setForceRefresh(false);
            }
            tabulatorRef.current = new Tabulator("#grid_" + props.tableName, {
                data: tabledata,
                columns: defaultColumns,
                layout: "fitColumns",
                layoutColumnsOnNewData: true,
                selectable: 1,
                selectableRollingSelection: false,
                scrollToRowPosition: "center",

            }).on("rowClick", handleRowClick);

        }, [forceRefresh]);


        useEffect(() => {
            if (parameters.state && parameters.state.entityCode && parameters.state.entityCode.length > 0) {

                var row: any;
                var affiliatedEntityCode: string;
                if (tabulatorRef.current && tabulatorRef.current.table) {

                    var table = tabulatorRef.current.table;
                    var rowToSelect: string = parameters.state.entityCode;
                    table.deselectRow();

                    //scroll to the row with the error
                    table.scrollToRow(rowToSelect, "top", true)
                        .then(function () {
                            //run code after row has been scrolled to
                            table.selectRow(rowToSelect);
                            row = getRowFromDimensionId(table, rowToSelect);

                            // Load the current affiliated entity code to redux to be used into other components
                            affiliatedEntityCode = row.getData().dimensionCode;
                            dispatch(setSelectedAffiliatedEntityCode(affiliatedEntityCode));

                        });

                }

            }
        }, [getEntityCodeFromUrl()]);


        React.useEffect(() => {
            // reset the states when destroying the component
            return function cleanup() {

                // Saving pending changes to AfrSlice
                utilityHelper.saveDeferredChanges(dispatch);

            }
        }, []);

        return (

            <React.Fragment>
                <div id={"grid_" + props.tableName} ref={ele => { tabulatorRef.current = ele }} />
            </React.Fragment>
        );
    }
);

export { ColumnAccountEditorGrid };
